import React, { useState, useEffect } from 'react';
import { SettingsIcon, CloseIcon, TrashIcon } from './Icons';

function EdgeConfigDialog({ edge, onSave, onClose }) {
  const [label, setLabel] = useState('');
  const [condition, setCondition] = useState('');
  const [hasCondition, setHasCondition] = useState(false);

  useEffect(() => {
    if (edge) {
      setLabel(edge.label || '');
      setCondition(edge.data?.condition || '');
      setHasCondition(!!(edge.data?.condition));
    }
  }, [edge]);

  const handleSave = () => {
    const edgeData = {
      ...edge,
      label: label.trim() || undefined,
      data: hasCondition && condition.trim() 
        ? { ...edge.data, condition: condition.trim() }
        : { ...edge.data, condition: undefined }
    };
    onSave(edgeData);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this edge connection?')) {
      onSave({ ...edge, _delete: true });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SettingsIcon size={16} color="#60D2CC" />
            Configure Edge
          </h3>
          <button className="modal-close" onClick={onClose}>
            <CloseIcon size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Edge Info */}
          <div className="alert alert-info" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              Edge: {edge?.source} → {edge?.target}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              Configure how agents are connected in this workflow
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">Edge Label (Optional)</label>
            <input
              type="text"
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., 'primary path', 'fallback'"
            />
            <div className="input-hint">
              A descriptive label for this connection
            </div>
          </div>

          {/* Conditional Edge Section */}
          <div style={{ 
            marginTop: 'var(--spacing-lg)', 
            paddingTop: 'var(--spacing-lg)', 
            borderTop: '1px solid var(--border-primary)' 
          }}>
            <div className="input-group">
              <label 
                className="checkbox-label"
                style={{ fontSize: '14px', fontWeight: '600' }}
              >
                <input
                  type="checkbox"
                  checked={hasCondition}
                  onChange={(e) => setHasCondition(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                Add condition to this edge
              </label>
              <div className="input-hint">
                Only traverse this edge if the condition evaluates to true
              </div>
            </div>

            {hasCondition && (
              <>
                <div className="input-group" style={{ marginTop: 'var(--spacing-md)' }}>
                  <label className="input-label">Condition (Python Expression)</label>
                  <textarea
                    className="input input-mono"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    rows={3}
                    placeholder="state.status == 'success'"
                  />
                  <div className="input-hint">
                    Python expression that returns true/false. Access graph state via <code>state</code>
                  </div>
                </div>

                <div className="card" style={{ marginTop: 'var(--spacing-md)' }}>
                  <div className="card-header">
                    📚 Condition Examples
                  </div>
                  <div className="card-body" style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
                    <div style={{ marginBottom: '4px' }}>• <code>state.results.get('score', 0) {'>'} 0.8</code></div>
                    <div style={{ marginBottom: '4px' }}>• <code>state.completed_nodes {'>'} 3</code></div>
                    <div style={{ marginBottom: '4px' }}>• <code>state.status in ['success', 'partial']</code></div>
                    <div>• <code>len(state.results.get('items', [])) {'>'} 0</code></div>
                  </div>
                </div>

                <div className="alert alert-danger" style={{ marginTop: 'var(--spacing-md)' }}>
                  ⚠️ <strong>Security Note:</strong> Conditions are evaluated using <code>eval()</code>. 
                  Only use trusted expressions in production environments.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="btn btn-danger" onClick={handleDelete} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TrashIcon size={14} /> Delete Edge
          </button>
          
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save Config
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EdgeConfigDialog;
